import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Leaf, Sparkles, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import ShopifyBuyButton from '../components/ShopifyBuyButton';
import { recordQuizResult } from '../lib/api';

const blendResults = {
  ADALISSA: {
    name: 'ADALISSA',
    subtitle: 'Fine & Delicate Strands',
    desc: 'Lightweight moisture that hydrates without weighing hair down. Perfect for fine, delicate textures that need gentle nourishment.',
    image: '/images/adalissa-bottle.jpg',
    color: '#4A5D3A',
  },
  CORA: {
    name: 'CORA',
    subtitle: 'Defined Curls',
    desc: 'Enhances curl definition while leaving hair soft, hydrated, and full of shine. Your curls deserve to be celebrated.',
    image: '/images/cora-product.png',
    color: '#4A5D3A',
  },
  MARY: {
    name: 'MARY',
    subtitle: 'Protective Styles & Locs',
    desc: 'Nourishes the scalp and maintains healthy-looking hair in braids, twists, locs, and wigs. Protection meets nourishment.',
    image: '/images/mary-product.png',
    color: '#6b2737',
  },
  EVA: {
    name: 'EVA',
    subtitle: 'Dense Coils & Kinks',
    desc: 'Rich moisture for coily textures to help soften, nourish, and support healthy-looking hair. Made for your crown.',
    image: '/images/eva-bottle.jpg',
    color: '#6B7F5A',
  },
};

const questions = [
  {
    id: 'texture',
    title: 'What best describes your hair texture?',
    subtitle: 'Choose the closest match',
    options: [
      { label: 'Fine & Straight', value: 'fine', icon: '〰️' },
      { label: 'Wavy & Loose Curls', value: 'wavy', icon: '〰️' },
      { label: 'Curly & Coily', value: 'curly', icon: '〰️' },
      { label: 'Tight Coils & Kinks', value: 'coily', icon: '〰️' },
    ],
  },
  {
    id: 'concern',
    title: 'What is your #1 hair concern?',
    subtitle: 'Pick what matters most',
    options: [
      { label: 'Thinning or Flat Hair', value: 'thinning' },
      { label: 'Dryness & Lack of Shine', value: 'dryness' },
      { label: 'Frizz & Undefined Curls', value: 'frizz' },
      { label: 'Breakage & Split Ends', value: 'breakage' },
    ],
  },
  {
    id: 'routine',
    title: 'How do you usually style your hair?',
    subtitle: 'Your everyday look',
    options: [
      { label: 'Wear it down, minimal styling', value: 'minimal' },
      { label: 'Heat tools & blowouts', value: 'heat' },
      { label: 'Braids, twists, or locs', value: 'protective' },
      { label: 'Wash-and-go or twist-outs', value: 'natural' },
    ],
  },
  {
    id: 'goal',
    title: 'What result would make you happiest?',
    subtitle: 'Your hair dream',
    options: [
      { label: 'Lightweight volume & bounce', value: 'volume' },
      { label: 'Deep moisture & softness', value: 'moisture' },
      { label: 'Healthy scalp & growth', value: 'scalp' },
      { label: 'Defined, strong curls', value: 'definition' },
    ],
  },
];

function getRecommendation(answers) {
  const scores = { ADALISSA: 0, CORA: 0, MARY: 0, EVA: 0 };

  // Texture
  if (answers.texture === 'fine') scores.ADALISSA += 3;
  if (answers.texture === 'wavy') { scores.ADALISSA += 1; scores.CORA += 2; }
  if (answers.texture === 'curly') scores.CORA += 3;
  if (answers.texture === 'coily') scores.EVA += 3;

  // Concern
  if (answers.concern === 'thinning') scores.ADALISSA += 2;
  if (answers.concern === 'dryness') { scores.EVA += 2; scores.MARY += 1; }
  if (answers.concern === 'frizz') scores.CORA += 2;
  if (answers.concern === 'breakage') { scores.MARY += 2; scores.EVA += 1; }

  // Routine
  if (answers.routine === 'minimal') scores.ADALISSA += 2;
  if (answers.routine === 'heat') { scores.CORA += 1; scores.ADALISSA += 1; }
  if (answers.routine === 'protective') scores.MARY += 3;
  if (answers.routine === 'natural') { scores.EVA += 2; scores.CORA += 1; }

  // Goal
  if (answers.goal === 'volume') scores.ADALISSA += 2;
  if (answers.goal === 'moisture') { scores.EVA += 2; scores.CORA += 1; }
  if (answers.goal === 'scalp') scores.MARY += 2;
  if (answers.goal === 'definition') scores.CORA += 2;

  return Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
}

export default function FindYourBlend() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const handleSelect = (questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      setTimeout(() => {
        const rec = getRecommendation(newAnswers);
        setResult(blendResults[rec]);
        recordQuizResult({ recommended_blend: rec, answers: newAnswers }).catch(() => {});
      }, 400);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleRestart = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
  };

  const progress = result ? 100 : ((step + (answers[questions[step]?.id] ? 1 : 0)) / questions.length) * 100;

  return (
    <div style={{ minHeight: '100vh', background: '#F5F0E8', fontFamily: "'Manrope', sans-serif" }} data-testid="find-blend-page">
      {/* Header */}
      <header style={{ background: 'rgba(245,240,232,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(74,93,58,0.1)', padding: '1rem 2rem' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src="/images/coras-logo.png" alt="Cora's Naturals" style={{ height: 48 }} />
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-to-shop">
            <ArrowLeft size={14} style={{ marginRight: 4 }} /> Back to Shop
          </Button>
        </div>
      </header>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '3rem 1.5rem' }}>
        {/* Progress Bar */}
        <div style={{ height: 3, background: 'rgba(74,93,58,0.1)', borderRadius: 2, marginBottom: '3rem', overflow: 'hidden' }}>
          <motion.div
            style={{ height: '100%', background: '#4A5D3A', borderRadius: 2 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>

        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35 }}
            >
              {/* Step indicator */}
              <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6B7F5A', fontWeight: 600 }}>
                  Step {step + 1} of {questions.length}
                </span>
              </div>

              {/* Question */}
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 400, textAlign: 'center', color: '#2D3A24', marginBottom: '0.5rem' }} data-testid={`quiz-question-${step}`}>
                {questions[step].title}
              </h2>
              <p style={{ textAlign: 'center', color: '#6B7F5A', fontSize: '0.9rem', marginBottom: '2.5rem' }}>
                {questions[step].subtitle}
              </p>

              {/* Options */}
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {questions[step].options.map((opt) => {
                  const isSelected = answers[questions[step].id] === opt.value;
                  return (
                    <motion.button
                      key={opt.value}
                      onClick={() => handleSelect(questions[step].id, opt.value)}
                      style={{
                        width: '100%',
                        padding: '1.2rem 1.5rem',
                        border: `1px solid ${isSelected ? '#4A5D3A' : 'rgba(74,93,58,0.15)'}`,
                        background: isSelected ? 'rgba(74,93,58,0.06)' : 'white',
                        borderRadius: 8,
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: '1rem',
                        color: '#2D3A24',
                        fontWeight: 500,
                        transition: 'border-color 0.2s, background 0.2s',
                      }}
                      whileHover={{ scale: 1.01, borderColor: '#4A5D3A' }}
                      whileTap={{ scale: 0.99 }}
                      data-testid={`quiz-option-${opt.value}`}
                    >
                      {opt.label}
                    </motion.button>
                  );
                })}
              </div>

              {/* Back button */}
              {step > 0 && (
                <button
                  onClick={handleBack}
                  style={{ margin: '2rem auto 0', display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#6B7F5A', fontSize: '0.85rem' }}
                  data-testid="quiz-back"
                >
                  <ArrowLeft size={14} /> Previous question
                </button>
              )}
            </motion.div>
          ) : (
            /* Result */
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              style={{ textAlign: 'center' }}
              data-testid="quiz-result"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.4, type: 'spring' }}
                style={{ marginBottom: '1rem' }}
              >
                <Sparkles size={32} color="#4A5D3A" />
              </motion.div>

              <p style={{ fontSize: '0.7rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#6B7F5A', fontWeight: 600, marginBottom: '0.5rem' }}>
                Your Perfect Blend
              </p>

              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 400, color: result.color, marginBottom: '0.25rem' }}>
                {result.name}
              </h2>

              <p style={{ fontFamily: "'Dancing Script', cursive", fontSize: '1.4rem', color: '#4A5D3A', marginBottom: '2rem' }}>
                {result.subtitle}
              </p>

              <motion.img
                src={result.image}
                alt={result.name}
                style={{ width: 220, height: 280, objectFit: 'cover', borderRadius: 12, margin: '0 auto 2rem', display: 'block', boxShadow: '0 8px 32px rgba(74,93,58,0.15)' }}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              />

              <p style={{ maxWidth: 450, margin: '0 auto 2rem', color: '#2D3A24', lineHeight: 1.7 }}>
                {result.desc}
              </p>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <ShopifyBuyButton variant="cta" />
              </div>

              <button
                onClick={handleRestart}
                style={{ margin: '2rem auto 0', display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#6B7F5A', fontSize: '0.85rem' }}
                data-testid="quiz-restart"
              >
                <RotateCcw size={14} /> Retake Quiz
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
